import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useContext, useState } from "react";
import { Col, Form, Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { BsCash } from "react-icons/bs";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import Swal from "sweetalert2";
import LoadingButton from "../../../../../components/Loading/LoadingButton";
import {
  getByAuthService,
  postRequestByAuth,
} from "../../../../../function/helper";
import { SharedDataContext } from "../../../../../layouts/FetchSharedData";
import { ACCOUNT_WALLET_BALANCE_QUERY_KEY } from "../../sidebar/SidebarItems";
import BankAccountHandler from "./BankAccountHandler";
import styles from "./WalletFormRequest.module.css";
import CodList from "./CodList";
import RequestAmount from "./RequestAmount";
import { NumericFormat } from "react-number-format";

/*

  request types
    1 cod
    2 کیف پول
    3 اعتبار سرپرستی

*/

function WalletFormRequest({ onClose, onSubmit }) {
  const queryClient = useQueryClient();
  const { setSharedData } = useContext(SharedDataContext);
  const [codListModal, setCodListModal] = useState(false);
  const [modalRequestAmount, setModalRequestAmount] = useState(false);
  const [modalSupervisorRequestAmount, setModalSupervisorRequestAmount] =
    useState(false);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const { data: userInfo } = useQuery({
    queryKey: ["cod-request-user-info"],
    queryFn: () =>
      getByAuthService("/myaccount/v1/codrequest/check-info").then(res => {
        if (res && res.status === 200) {
          return {
            supervisor: res.data.supervisor,
            wallet: res.data.wallet,
          };
        }
      }),

    retry: 0,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: {
      supervisor: null,
      wallet: 0,
    },
  });

  const refreshDisplayedBalances = async (requestType, requestedAmount) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ACCOUNT_WALLET_BALANCE_QUERY_KEY,
      }),
      queryClient.invalidateQueries({ queryKey: ["cod-request-user-info"] }),
    ]);

    if (requestType === 2 && requestedAmount) {
      setSharedData(cur => {
        if (!cur?.user) {
          return cur;
        }

        return {
          ...cur,
          user: {
            ...cur.user,
            wallet: Math.max(0, (cur.user.wallet ?? 0) - requestedAmount),
          },
        };
      });
    }
  };

  const { isPending, mutate } = useMutation({
    mutationKey: ["new-cod-request"],
    mutationFn: data =>
      postRequestByAuth("/myaccount/v1/codrequest/add", data).then(async res => {
        if (res && res.status === 200) {
          await refreshDisplayedBalances(
            Number(data.requestType),
            Number(data.requested_amount)
          );

          Swal.fire({
            icon: "success",
            text: res.data.message,
            showCloseButton: false,
            confirmButtonText: "متوجه شدم",
          }).then(() => {
            reset();
            onSubmit();
            onClose();
          });
          return;
        }

        Swal.fire({
          icon: "error",
          text: res.data.message,
          showCloseButton: false,
          confirmButtonText: "متوجه شدم",
        });
      }),
  });

  return (
    <Fragment>
      <Modal centered size="xl" show={true} className={styles.modal}>
        <Modal.Header>
          <h4>فرم درخواست برداشت از کیف پول | COD</h4>
        </Modal.Header>

        <form onSubmit={handleSubmit(mutate)}>
          <Modal.Body>
            <Controller
              defaultValue={null}
              name="bankAccountId"
              control={control}
              rules={{
                required: { value: true, message: "انتخاب حساب الزامی" },
              }}
              render={({ field: { value, onChange } }) => (
                <BankAccountHandler
                  selectBankAccountId={value}
                  setSelectBankAccountId={onChange}
                />
              )}
            />

            {errors.bankAccountId && (
              <p className="text-danger fs-small">
                {errors.bankAccountId.message}
              </p>
            )}

            <div className="w-100 d-flex flex-wrap row-gap-1">
              <Col xs={12} sm={6} lg={4} className="px-2">
                <Form.Label>
                  مبلغ درخواستی<span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  control={control}
                  name="requested_amount"
                  rules={{
                    required: { value: true, message: "وجه الزامی" },
                    min: {
                      value: 500000,
                      message: "حداقل مبلغ درخواستی 500 هزار ریال است",
                    },
                  }}
                  render={({ field: { ref } }) => (
                    <NumericFormat
                      getInputRef={ref}
                      suffix=" ريال"
                      disabled={true}
                      className="form-control"
                      value={getValues("requested_amount") ?? ""}
                      thousandSeparator={true}
                    />
                  )}
                />
                {errors.requested_amount && (
                  <p className="m-0 fs-small text-danger">
                    {errors.requested_amount.message}
                  </p>
                )}
              </Col>
            </div>

            <div className="mt-4">
              <button
                className="buttonUi btn-secondary-avaex-outline"
                type="button"
                disabled={[2, 3].includes(getValues("requestType"))}
                onClick={() => setCodListModal(true)}
              >
                <span>
                  <FaMoneyBill1Wave />
                </span>
                <span className="me-2">درخواست مبلغ COD</span>
              </button>

              <button
                className="buttonUi btn-secondary-avaex-outline me-3"
                type="button"
                disabled={[1, 3].includes(getValues("requestType"))}
                onClick={() => setModalRequestAmount(true)}
              >
                <span>
                  <RiMoneyDollarCircleLine />
                </span>
                <span className="me-2">درخواست مبلغ از کیف پول</span>
              </button>

              {userInfo.supervisor !== null && (
                <button
                  type="button"
                  className="buttonUi btn-secondary-avaex-outline me-3"
                  disabled={[1, 2].includes(getValues("requestType"))}
                  onClick={() => setModalSupervisorRequestAmount(true)}
                >
                  <span>
                    <BsCash />
                  </span>
                  <span className="me-2">درخواست مبلغ از اعتبار سرپرستی</span>
                </button>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            {isPending && (
              <LoadingButton
                className="btn-success-style"
                center
                title="در حال ثبت"
              />
            )}
            {!isPending && (
              <button type="submit" className="buttonUi btn-success-style">
                ثبت
              </button>
            )}
            <button
              type="button"
              disabled={isPending}
              className="btn btn-danger"
              onClick={onClose}
            >
              خروج
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {codListModal && (
        <CodList
          close={() => setCodListModal(false)}
          set={({ amount, numberParcel }) => {
            setValue("requested_amount", amount);
            setValue("numberParcel", numberParcel);
            setValue("requestType", 1);
          }}
        />
      )}

      {modalRequestAmount && (
        <RequestAmount
          close={() => setModalRequestAmount(false)}
          value={getValues("requested_amount")}
          setValue={amount => {
            setValue("requested_amount", amount);
            setValue("requestType", 2);
          }}
          wallet={userInfo.wallet}
        />
      )}

      {modalSupervisorRequestAmount && (
        <RequestAmount
          close={() => setModalSupervisorRequestAmount(false)}
          value={getValues("requested_amount")}
          setValue={amount => {
            setValue("requested_amount", amount);
            setValue("requestType", 3);
          }}
          wallet={userInfo.supervisor.credit}
          isSupervisorModal={true}
        />
      )}
    </Fragment>
  );
}

export default WalletFormRequest;
